import {
  EVENT_COOKIE_CONSENT_SUBMITTED,
  EVENT_COOKIE_CONSENT_VIEWED,
  EVENT_CTA_CLICKED,
  EVENT_DATA_LAYER_INITIALIZED,
  EVENT_DATA_LAYER_READY,
  EVENT_ELIGIBLE_BENEFIT_CLICKED,
  EVENT_ERROR,
  EVENT_JOURNEY_STARTED,
  EVENT_JOURNEY_UPDATED,
  EVENT_LANDING_PAGE_VIEWED,
  EVENT_LEAD_FORM_SUBMITTED,
  EVENT_NPS_5,
  EVENT_PAGE_NOT_FOUND,
  EVENT_PAGE_VIEWED,
  EVENT_SECONDARY_OFFERS_FORM_SUBMITTED,
  EVENT_SECONDARY_OFFERS_STEP_SUBMITTED,
  EVENT_SECONDARY_OFFER_CLICKED,
  EVENT_STEP_SUBMITTED,
  EVENT_STEP_VIEWED,
  EVENT_THANK_YOU_PAGE_VIEWED,
  EVENT_TRIGGER_NON_ESSENTIAL_SCRIPTS,
} from "@shared/constants";

import { logDebug } from "@shared/functions/log";

import type { TUserAgentDetails } from "@server/front-end-api/postVisit";

import type { Props as JourneyProps } from "@client/classes/models/journey";

import type EventsType from "@packages/types/events";
import type LeadForm from "@packages/types/lead-form";
import type SecondaryOffersForm from "@packages/types/secondary-offers-form";

// Type mapping for events and their expected data types
export interface EventDataMap {
  [EVENT_COOKIE_CONSENT_SUBMITTED]: {
    data: EventsType.CookieConsentSubmitted;
    location: Location;
  };
  [EVENT_COOKIE_CONSENT_VIEWED]: Location;
  [EVENT_CTA_CLICKED]: {
    data: EventsType.CTAClicked;
    location: Location;
  };
  [EVENT_DATA_LAYER_INITIALIZED]: EventsType.DataLayerInitialized;
  [EVENT_DATA_LAYER_READY]: undefined;
  [EVENT_ELIGIBLE_BENEFIT_CLICKED]: {
    data: EventsType.EligibleBenefitClicked;
    location: Location;
  };
  [EVENT_ERROR]: { data: EventsType.ErrorData; location: Location };
  [EVENT_JOURNEY_STARTED]: JourneyProps;
  [EVENT_JOURNEY_UPDATED]: undefined;
  [EVENT_LANDING_PAGE_VIEWED]: {
    userAgentDetails: TUserAgentDetails;
  } & Location;
  [EVENT_STEP_VIEWED]: {
    data: LeadForm.Step;
    location: Location;
  };
  [EVENT_STEP_SUBMITTED]: {
    data: Omit<LeadForm.StepValues, "cypress">;
    location: Location;
  };
  [EVENT_LEAD_FORM_SUBMITTED]: {
    data: EventsType.LeadFormSubmitted;
    location: Location;
  };
  [EVENT_PAGE_NOT_FOUND]: Location;
  [EVENT_PAGE_VIEWED]: Location;
  [EVENT_SECONDARY_OFFER_CLICKED]: {
    data: EventsType.SecondaryOfferClicked;
    location: Location;
  };
  [EVENT_SECONDARY_OFFERS_FORM_SUBMITTED]: {
    data: EventsType.SecondaryOffersFormSubmitted;
    location: Location;
  };
  [EVENT_SECONDARY_OFFERS_STEP_SUBMITTED]: {
    data: Omit<SecondaryOffersForm.StepValues, "cypress">;
    location: Location;
  };
  [EVENT_THANK_YOU_PAGE_VIEWED]: {
    data: EventsType.ThankYouPage;
    location: Location;
  };
  [EVENT_TRIGGER_NON_ESSENTIAL_SCRIPTS]: undefined;
  [EVENT_NPS_5]: {
    data: EventsType.NPSRating;
    location: Location;
  };
}

/**
 * Creates an extendible Publish-Subscribe (Pub-Sub) pattern.
 *
 * @see https://github.com/mroderick/PubSubJS#readme
 */
export default class PubSub {
  /**
   * A nested array containing events and their subscribers
   */
  private events: {
    [K in keyof EventDataMap]?: ((event: K, data: EventDataMap[K]) => void)[];
  } = {};

  /**
   * An array of events that were fired throughout the course of the browsing session.
   */
  private history: { event: string; data: unknown; source: string }[] = [];

  /**
   * Defines the "Data Layer" source saved in the history object.
   */
  private source = "DataLayer";

  // Function overloads for type-safe subscribing

  // on logic can be leveraged to fulfill subscribe
  subscribe<K extends keyof EventDataMap>(
    event: K,
    handler: (event: K, data: EventDataMap[K]) => void,
  ): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }

    this.events[event].push(handler);
  }

  // off logic can be leveraged to fulfill unsubscribe
  unsubscribe<K extends keyof EventDataMap>(
    event: K,
    handler: (event: K, data: EventDataMap[K]) => void,
  ): void {
    if (this.events[event]) {
      const index = this.events[event].findIndex((item) => item === handler);
      this.events[event].splice(index, 1);
    }
  }

  // Function overloads for type-safe publishing
  publish<K extends keyof EventDataMap>(
    event: K,
    data?: EventDataMap[K] | undefined,
  ): void;

  // emit logic can be leveraged to fulfill unsubscribe
  publish<K extends keyof EventDataMap>(
    event: K,
    data?: EventDataMap[K] | undefined,
  ): void {
    // publish events to `document.documentElement` in order to let Cypress tests listen for DataLayer events
    document.documentElement.dispatchEvent(new Event(event));

    this.log(event, data, this.source);

    if (this.events[event]) {
      for (const handler of this.events[event]) {
        handler(event, data || ({} as EventDataMap[K]));
      }
    }

    // publish event to document.documentElement so Cypress or other external services can hook-in
    document.documentElement.dispatchEvent(new Event(event));
  }

  // log debugging messages to console
  log(event: string, data: unknown, source: string) {
    this.history.push({ event, data, source });

    logDebug({
      message: [
        `Event '${event}' was`,
        source === this.source ? "fired" : `sent to ${source}`,
      ].join(" "),
      event: {
        name: event,
        destination: source,
        meta: data as object | undefined,
      },
    });
  }
}
